import { Button } from '@/components/UI/Button'
import { Dialog } from '@/components/UI/Dialog'
import { IconChevronRight } from '@tabler/icons-react'
import { memo } from 'react'

export type StampDetailInfo = {
  id: number
  name: string
  image: string | null
  point: number
  /** 取得済みか */
  isHas: boolean
  spots: {
    id: number
    name: string
    /** スポットサムネイル画像URL */
    image?: string | null
    address?: string
    zip?: string
  }[]
}

type Props = {
  stamp: StampDetailInfo | null
  isOpen: boolean
  onClose: () => void
  /** スポット行タップ時に呼ばれる。spotId を渡す */
  onCheckin: (spotId: number) => void
  /** スタンプの形状（0: 丸, 1: 四角）。省略時は 0（丸）として扱う */
  stampShape?: 0 | 1
}

/** スタンプ詳細ダイアログ */
export const DialogStampDetail = memo(({ stamp, isOpen, onClose, onCheckin, stampShape }: Props) => {
  if (!stamp) return null

  const isSquare = stampShape === 1
  const stampImageClass = isSquare ? 'w-32 h-32 object-cover' : 'w-32 h-32 object-cover rounded-full'
  const stampPlaceholderClass = isSquare
    ? 'w-32 h-32 bg-gray-100 flex items-center justify-center text-gray-400 text-xs'
    : 'w-32 h-32 bg-gray-100 rounded-full flex items-center justify-center text-gray-400 text-xs'

  return (
    <Dialog selectorState={isOpen} closeFunc={onClose} title="スタンプ詳細">
      <div className="flex flex-col items-center gap-4">
        {/* スタンプ画像 */}
        {stamp.image ? (
          <img src={stamp.image} alt={stamp.name} className={stampImageClass} />
        ) : (
          <div className={stampPlaceholderClass}>No Image</div>
        )}

        {/* スタンプ名 */}
        <h2 className="text-[16px] font-bold text-black text-center mb-4">{stamp.name}</h2>

        {/* スポット一覧 */}
        <div className="w-full">
          {/* 獲得可能スポット */}
          <p className="text-[16px] font-bold text-black text-center">獲得可能スポット</p>
          {stamp.spots.map((spot) => (
            <button
              key={spot.id}
              type="button"
              onClick={() => {
                onCheckin(spot.id)
                onClose()
              }}
              className="w-full flex items-center gap-3 px-0 mt-2 pb-2 h-20 border-b border-[#616161] last:border-b-0 bg-transparent text-left"
            >
              {/* スポットサムネイル */}
              {spot.image ? (
                <img src={spot.image} alt={spot.name} className="w-16 h-16 rounded-2xl object-cover shrink-0" />
              ) : (
                <div className="w-16 h-16 rounded-2xl bg-gray-200 shrink-0 flex items-center justify-center text-gray-400 text-xs">
                  No Img
                </div>
              )}
              {/* スポット情報 */}
              <div className="flex-1 min-w-0">
                <p className="text-[16px] font-bold text-black leading-6 truncate">{spot.name}</p>
                <p className="text-[11px] font-bold text-black leading-4">{spot.address}</p>
              </div>
              <IconChevronRight size={24} className="shrink-0 text-[#cbcbce]" aria-hidden="true" />
            </button>
          ))}
        </div>

        {/* OK ボタン */}
        <div className="flex justify-center w-full">
          <Button label="閉じる" variant="primary" size="md" onClick={onClose} fullWidth />
        </div>
      </div>
    </Dialog>
  )
})
